import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const ChangeMobileNumberPage = () => {
    const { employeeId } = useParams();
    const navigate = useNavigate();

    const [newMobile, setNewMobile] = useState("");
    const [confirmMobile, setConfirmMobile] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";

    // Only 10 digits allowed
    const validateMobile = (mobile) => /^\d{10}$/.test(mobile);

    const handleSubmit = () => {
        setError("");
        setLoading(true); // move loading earlier

        if (!newMobile || !confirmMobile) {
            setError("Please enter and confirm mobile number");
            setLoading(false);
            return;
        }

        if (newMobile !== confirmMobile) {
            setError("Mobile numbers do not match");
            setLoading(false);
            return;
        }

        if (!validateMobile(newMobile)) {
            setError("Please enter a valid 10-digit mobile number");
            setLoading(false);
            return;
        }

        // ────────────────────────────────────────────────
        // Start chain
        updateStaff()
            .catch(() => updateEmployee()) // if staff fails (network/error)
            .then((result) => {
                if (result?.success) return result; // early exit if already succeeded
                return updateEmployee(); // try next
            })
            .catch(() => updateCandidate()) // if employee fails
            .then((result) => {
                if (result?.success) return result;
                return updateCandidate();
            })
            .then((finalResult) => {
                setLoading(false);
                if (finalResult?.success) {
                    alert(`Mobile number updated successfully (${finalResult.type})`);
                    navigate(finalResult.type === "Staff" ? "/enablestaff" : -1);
                } else {
                    setError("Failed to update mobile number in any system");
                }
            })
            .catch((err) => {
                setLoading(false);
                console.error("All update attempts failed:", err);
                setError("Failed to update mobile number. Please try again later.");
            });
    };

    // ────────────────────────────────────────────────
    // Helper functions (cleaner + return promise + success flag)

    const updateStaff = () => {
        return fetch(`${apiBaseUrl}/updatestaffmobile`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                staffId: employeeId,
                mobileNumber: newMobile,
            }),
        })
            .then((res) => res.json())
            .then((data) => {
                const success = data.message?.toLowerCase().includes("success") || data.success === true;
                if (success) {
                    return { success: true, type: "Staff" };
                }
                console.log("Staff update failed:", data);
                return { success: false };
            });
    };

    const updateEmployee = () => {
        return fetch(`${apiBaseUrl}/employee/update-mobile/${employeeId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mobile: newMobile }),
        })
            .then((res) => res.json())
            .then((data) => {
                const success = data.message?.toLowerCase().includes("success") || data.success === true;
                if (success) {
                    return { success: true, type: "Employee" };
                }
                console.log("Employee update failed:", data);
                return { success: false };
            });
    };

    const updateCandidate = () => {
        return fetch(`${apiBaseUrl}/updateCandidateMobile/${employeeId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mobile: newMobile }),
        })
            .then((res) => res.json())
            .then((data) => {
                const success = data.message?.toLowerCase().includes("success") || data.success === true;
                if (success) {
                    return { success: true, type: "Candidate" };
                }
                console.log("Candidate update failed:", data);
                return { success: false };
            });
    };

    const handleInputChange = (setter) => (e) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
        setter(value);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Change Mobile Number</h2>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-center">
                        {error}
                    </div>
                )}

                <div className="space-y-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">New Mobile Number</label>
                        <input
                            type="tel"
                            value={newMobile}
                            onChange={handleInputChange(setNewMobile)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter 10-digit number"
                            maxLength={10}
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-2">Confirm Mobile Number</label>
                        <input
                            type="tel"
                            value={confirmMobile}
                            onChange={handleInputChange(setConfirmMobile)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Re-enter number"
                            maxLength={10}
                            disabled={loading}
                        />
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className={`w-full py-3 px-4 text-white font-semibold rounded-lg transition ${
                            loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {loading ? "Updating..." : "Update Mobile Number"}
                    </button>

                    <button
                        onClick={() => navigate(-1)}
                        disabled={loading}
                        className="w-full py-3 px-4 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangeMobileNumberPage;
